import {createSelector, type Selector} from 'reselect';

import {type Ref as PanelRef} from '../../../state/views/panel/types';
import {type Ref as PanelBankConfigRef} from '../../../state/views/panelBankConfig/types';
import {
  selectSectionMediaSliderKeyFromSection,
  selectSectionMediaSliderSyncFromSection,
} from '../../../state/views/panelBankSectionConfig/selectors';
import {type Ref as SectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {makeOptionalPartSelector} from '../../../state/views/selectors';
import {type RootState} from '../../../types/redux';
import {type LayedOutPanel} from '../../../util/panelTypes';

export type PanelSyncSliderInfo = {
  panelRef: PanelRef;
  viewType: LayedOutPanel['viewType'];
  panelSliderKey: string;
};

export type SectionSyncSliderData = {
  panelSyncInfo: PanelSyncSliderInfo[];
};

export const EMPTY_SECTION_SYNC_SLIDER_DATA: SectionSyncSliderData = {
  panelSyncInfo: [],
};

export type WorkspaceSyncSliderData = {
  panelSyncInfo: PanelSyncSliderInfo[];
};

export const EMPTY_WORKSPACE_SYNC_SLIDER_DATA: WorkspaceSyncSliderData = {
  panelSyncInfo: [],
};

const selectPanelBankConfigSectionRefs =
  (panelBankConfigRef: PanelBankConfigRef) => (state: RootState) =>
    state.views.parts[panelBankConfigRef.type][panelBankConfigRef.id]
      .sectionRefs;

const selectPanelBankSections = (state: RootState) =>
  state.views.parts['panel-bank-section-config'];

const selectPanelParts = (state: RootState) => state.views.parts.panel;

function getPanelSliderKey(
  panel: LayedOutPanel,
  inheritedSliderKey: string
): string {
  if (panel.viewType === 'Media Browser' && panel.config.mediaSliderKey) {
    return panel.config.mediaSliderKey;
  }
  return inheritedSliderKey;
}

export const makeSectionSyncSliderDataSelector = (
  sectionRef: SectionRef,
  sectionStepSliderKey: string
): Selector<RootState, SectionSyncSliderData> => {
  return createSelector(
    makeOptionalPartSelector(sectionRef),
    selectPanelParts,
    (section, panelParts) => {
      const panelSyncInfo: PanelSyncSliderInfo[] = [];

      for (const panelRef of section?.panelRefs ?? []) {
        const panel = panelParts[panelRef.id];
        panelSyncInfo.push({
          panelRef,
          viewType: panel.viewType,
          panelSliderKey: getPanelSliderKey(panel, sectionStepSliderKey),
        });
      }

      return {panelSyncInfo};
    }
  );
};

export const makeMaybeSectionSyncSliderDataSelector = (
  sectionRef?: SectionRef,
  sectionStepSliderKey = '_step'
) => {
  if (sectionRef == null) {
    return () => EMPTY_SECTION_SYNC_SLIDER_DATA;
  }

  return makeSectionSyncSliderDataSelector(sectionRef, sectionStepSliderKey);
};

export const makeWorkspaceSyncSliderDataSelector = (
  panelBankConfigRef: PanelBankConfigRef,
  workspaceStepSliderKey: string,
  includeAllSections = false
): Selector<RootState, WorkspaceSyncSliderData> => {
  return createSelector(
    selectPanelBankConfigSectionRefs(panelBankConfigRef),
    selectPanelBankSections,
    selectPanelParts,
    (sectionRefs, sections, panelParts) => {
      const panelSyncInfo: PanelSyncSliderInfo[] = [];

      for (const sectionRef of sectionRefs) {
        const section = sections[sectionRef.id];
        if (
          !includeAllSections &&
          selectSectionMediaSliderSyncFromSection(section) !== undefined
        ) {
          continue;
        }

        const inheritedSliderKey =
          selectSectionMediaSliderKeyFromSection(section) ??
          workspaceStepSliderKey;

        for (const panelRef of section.panelRefs) {
          const panel = panelParts[panelRef.id];
          panelSyncInfo.push({
            panelRef,
            viewType: panel.viewType,
            panelSliderKey: getPanelSliderKey(panel, inheritedSliderKey),
          });
        }
      }

      return {panelSyncInfo};
    }
  );
};

export const makeMaybeWorkspaceSyncSliderDataSelector = (
  panelBankConfigRef: PanelBankConfigRef | undefined,
  workspaceStepSliderKey: string,
  includeAllSections = false
) => {
  if (panelBankConfigRef == null) {
    return () => EMPTY_WORKSPACE_SYNC_SLIDER_DATA;
  }

  return makeWorkspaceSyncSliderDataSelector(
    panelBankConfigRef,
    workspaceStepSliderKey,
    includeAllSections
  );
};
